import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { PromotionModel } from '@bytel/bytel-sales';

import { MainCartService } from '@services/main-cart.service';
import { PromoCodeCompatibilityService } from '@services/promo-code-compatibility.service';
import { StepperService } from '@services/stepper.service';

import { Subject, throwError } from 'rxjs';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';

import { DataCyDirective } from '../../../common/directives/data-cy.directive';
import { CartStep } from '../../stepper/steps/cart.step';
import { CrossellStep } from '../../stepper/steps/crossell.step';
import { Step } from '../../stepper/steps/step';

@Component({
    selector: 'checkout-coupon',
    templateUrl: './coupon.component.html',
    styleUrl: 'coupon.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, NgClass, DataCyDirective],
})
export class CouponComponent implements OnDestroy, OnInit {
    public coupon: PromotionModel;
    public loading: boolean = false;
    public showError: boolean = false;
    public errorMessage: string;
    public showCouponInput: boolean = false;
    public stepsToDisplay: string[] = [CartStep.key, CrossellStep.key];
    public couponForm: FormControl<string | null>;
    private _ngUnsubscribe = new Subject();
    public couponHasBeenChecked: boolean = false;

    constructor(
        private cartService: MainCartService,
        private stepperService: StepperService,
        private ref: ChangeDetectorRef,
        private promoCodeCompatibilityService: PromoCodeCompatibilityService,
    ) {}

    public ngOnInit(): void {
        this.couponForm = new FormControl(null, Validators.required);
        this.setCurrentCoupon();

        // Check if promo code zone has to be displayed
        this._checkPromoCodeDisplay();

        // when step change
        this.stepperService.currentStepSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
            this._checkPromoCodeDisplay();
        });

        this.cartService.couponObs.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
            this.setCurrentCoupon();
            this.ref.detectChanges();
        });

        this.couponForm.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
            this.couponHasBeenChecked = false;
        });
    }

    public ngOnDestroy(): void {
        this.ref.detach();
        this._ngUnsubscribe.next(null);
        this._ngUnsubscribe.complete();
    }

    public checkCouponCode(): void {
        this.couponHasBeenChecked = true;
        if (this.couponForm.valid) {
            const couponCode: string = this.couponForm.value;
            if (couponCode.length && !this.loading) {
                this.loading = true;

                this.promoCodeCompatibilityService
                    .checkAddCoupon(this.cartService.cartModel, couponCode)
                    .pipe(
                        catchError((error) => {
                            console.log(error);
                            return throwError(error);
                        }),
                    )
                    .subscribe(
                        () => {
                            this._handleCoupon(true);
                        },
                        (error) => {
                            if (error.toAdd && error.toDelete) {
                                // incompatible, display modal
                                this.promoCodeCompatibilityService
                                    .showModal(error.toAdd, error.toDelete)
                                    .pipe(mergeMap((coupon) => this.cartService.setCouponPromotion(coupon)))
                                    .subscribe(
                                        () => {
                                            this._handleCoupon(true);
                                        },
                                        () => {
                                            this._handleCoupon();
                                        },
                                    );
                            } else {
                                this.errorMessage = error?.error?.error_description || 'Ce code promo est invalide.';
                                this.showError = true;
                                this._handleCoupon();
                            }
                        },
                    );
            }
        }
    }

    public setCurrentCoupon(): void {
        this.coupon = this.cartService.cartModel.promotions.coupon;
    }

    public removeCoupon(): void {
        if (this.coupon) {
            this.cartService
                .removeCoupon()
                .pipe(
                    mergeMap(() => this.cartService.removeCouponPromotion()),
                    catchError(() => this.cartService.removeCouponPromotion()),
                )
                .subscribe(() => {
                    this.coupon = null;
                    this.ref.detectChanges();
                });
        }
    }

    public _checkPromoCodeDisplay(): void {
        const currentStep: Step = this.stepperService.currentStep;
        this.showCouponInput = this.stepsToDisplay.includes(currentStep.key);
        this.ref.detectChanges();
    }

    private _handleCoupon(setCoupon: boolean = false): void {
        if (setCoupon) {
            this.setCurrentCoupon();
        }
        this.loading = false;
        this.ref.detectChanges();
    }
}
